// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

export interface TransformationsOptions {
    width?: number | string;
    quality?: "best" | "good" | "eco" | "low";
    mobileWidth?: number;
    tabletWidth?: number;
}

const transformationsRegex = /([a-z]_[^/]*)(?=\/)/;
const transformationsCleanupRegex = /([a-z]_[^/]*)(\/)/;

const getOptionsResult = (url: string, customOptions = "") => {
    const urlOptionsMatch = transformationsRegex.exec(url);
    if (!urlOptionsMatch) {
        return customOptions;
    }

    const urlOptionsArray = urlOptionsMatch[0].split(",");
    const customOptionsArray = customOptions.split(",").filter(Boolean);
    const urlOptionsHash: any = {};
    const customOptionsHash: any = {};

    customOptionsArray.forEach((opt, index) => {
        const [key, value] = opt.split("_");
        customOptionsHash[key] = value;
    });

    urlOptionsArray.forEach((opt, index) => {
        const [key, value] = opt.split("_");
        urlOptionsHash[key] = customOptionsHash[key] || value;
    });

    const urlOptions = Object.keys(urlOptionsHash).map(key => `${key}_${urlOptionsHash[key]}`);

    // remove dublicated options
    const uniqueOptions = Array.from(new Set([...customOptionsArray, ...urlOptions])).join(",");

    return uniqueOptions;
};

const cleanUrl = (url: string, from: "start" | "end", regex?: RegExp) => {
    let urlToClean = regex ? url.replace(regex, "") : url;
    if (from === "start") {
        urlToClean = urlToClean.startsWith("/") ? urlToClean.slice(1, urlToClean.length) : urlToClean;
    } else {
        urlToClean = urlToClean.endsWith("/") ? urlToClean.slice(0, urlToClean.length - 1) : urlToClean;
    }
    return urlToClean;
};

/**
 *
 * @param url: image url
 * @param baseUrl: Cloudinary url domain - include slash at the end
 * @param transformations: image transformation
 */
export const getCloudinaryUrl = (
    url?: string,
    baseUrl?: string,
    transformations?: TransformationsOptions,
    urlOptions?: string,
): string | undefined => {
    const unNeededUrl = "image/upload/";
    let options = "f_auto";
    if (urlOptions) {options = `${options},${urlOptions}`};

    let isSvg = false;

    if (!url || !baseUrl || !url.startsWith(baseUrl)) {
        return url;
    }

    let specificUrl = url.replace(unNeededUrl, "").replace(baseUrl, "");

    // If this is an svg file, we need to remove f_auto option which will
    // rasterize the vector graphic and instead use fl_sanitize to prevent
    // automatically downloading the svg instead of displaying correctly as img src.
    if (url.endsWith(".svg")) {
        isSvg = true;
        options = "fl_sanitize";
    }

    // If transformations are present and this is not a vector graphic, add transformations to options
    if (transformations && !isSvg) {
        if (transformations.width) {
            options = `${options},w_${transformations.width}`;
        }
        if (transformations.quality) {
            options = `${options},q_auto:${transformations.quality}`;
        }
    }

    // Remove duplicated transformations
    const optionsParts = options.split(",");
    optionsParts.forEach((option, index) => {
        if (specificUrl.includes(option)) {
            optionsParts[index] = "";
        }
    });
    options = optionsParts.filter(Boolean).join(",");

    // remove the transformations & forawrd slash from the start of the url
    specificUrl = cleanUrl(specificUrl, "start");
    // remove the forward slash from the end of the url
    const cleanedBaseUrl = cleanUrl(baseUrl, "end");
    // return the resulted url
    return [cleanedBaseUrl, options, specificUrl].filter(Boolean).join("/");
};
